import AccessAlarmsRoundedIcon from "@mui/icons-material/AccessAlarmsRounded"
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded"
import { Box, Divider, Typography } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, useState } from "react"

import AdditionalKpiCard from "../../components/dashboard/additionalKpiCard"
import BarChart from "../../components/dashboard/barChart"
import DashboardPanelFrame from "../../components/dashboard/dashboardPanelFrame"
import ListDevices from "../../components/dashboard/listDevices"
import SingleKpiCard from "../../components/dashboard/singleKpiCard"
import StackedBarChart, {
    Data,
} from "../../components/dashboard/stackedBarChart"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import { ApprovalsIcon } from "../../components/icons/icons"
import DashboardHomepageSkeleton from "../../components/loading/skeletons/dashboardHomepageSkeleton"
import ReleaseSnackbar from "../../components/snackbar/releaseSnackbar"
import { AppCardHeader } from "../../components/surfaces/appCard"
import {
    DNO_REVIEWER,
    DISPLAY_ROLES,
    DNO_APPROVED,
} from "../../constants/roles"
import { TT_DEVICES_ALERT, TT_DEVICES_APPROVED } from "../../constants/tooltips"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    DNO_APPLICATIONS_ESCALATIONS,
    DNO_APPLICATIONS_NOTIFICATIONS,
} from "../../router/routes"
import useGetDnoHomepageData from "../../services/middleware/hooks/dnoInterface/useGetDnoHomepageData"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

export const FIXED_CHART_HEIGHT = 340

/**
 * Renders the dashboard for DNO users which is displayed as their homepage
 */
const DnoHomepage = (): ReactElement => {
    const { firstName, groups } = useGetUserData()
    const { data, error, isLoading } = useGetDnoHomepageData()

    const [timeFrame, setTimeFrame] = useState("totLctsByDay")

    const handleToggleButtonClick = (time: string): void => {
        setTimeFrame(time)
    }

    const isDnoReviewer = groups.includes(DNO_REVIEWER)

    const [open, setOpen] = useState(true)

    if (isLoading) {
        return <DashboardHomepageSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={error} />
    }
    if (!data) {
        return <Typography> KPI Currently Unavailable</Typography>
    }

    return (
        <>
            {/* <ReleaseSnackbar open={open} setOpen={setOpen} /> */}
            <Grid container spacing={GRID_SPACING}>
                <Grid xs={12}>
                    <DashboardPanelFrame
                        userName={firstName}
                        permission={DISPLAY_ROLES[DNO_APPROVED]}
                        additionalPermission={isDnoReviewer}
                    />
                </Grid>

                <Grid md={4} xs={12}>
                    <SingleKpiCard
                        Icon={AccessAlarmsRoundedIcon}
                        value={data.responseTime30DayAvg}
                        title="30-day Escalation Average Response Time (days)"
                    />
                </Grid>
                <Grid md={4} xs={12}>
                    <SingleKpiCard
                        Icon={ApprovalsIcon}
                        value={data.totOpenEscalations}
                        title="Current Open Escalations"
                        link={
                            isDnoReviewer
                                ? DNO_APPLICATIONS_ESCALATIONS
                                : undefined
                        }
                    />
                </Grid>
                <Grid md={4} xs={12}>
                    <SingleKpiCard
                        Icon={FormatListNumberedRoundedIcon}
                        value={data.totAutoApprovalRate}
                        title="Auto-Approval Rate (All Time)"
                        link={
                            isDnoReviewer
                                ? DNO_APPLICATIONS_NOTIFICATIONS
                                : undefined
                        }
                    />
                </Grid>
                <Grid lg={3} md={4} xs={12}>
                    <AdditionalKpiCard
                        cardTitle="Other Metrics"
                        cardDescription="Additional KPIs for the selected time frame"
                        data={data}
                    />
                </Grid>
                <Grid lg={3} md={8} xs={12}>
                    <Box height="100%">
                        <BarChart
                            title="Resolved Applications"
                            description="Breakdown of resolved applications by status (all time)"
                            data={{
                                totLctsByResolvedStatus:
                                    data.totLctsByResolvedStatus,
                            }}
                        />
                    </Box>
                </Grid>
                <Grid lg={6} md={12} xs={12}>
                    <StackedBarChart
                        title="Application Trend"
                        description="Total applications over a selected time frame"
                        data={
                            data[
                                timeFrame as keyof typeof data
                            ] as unknown as Data[]
                        }
                        onClick={handleToggleButtonClick}
                    />
                </Grid>
                <Grid xs={12}>
                    <Divider sx={{ marginY: 3 }} />
                    <AppCardHeader
                        title="ENA Asset Register Summary Information"
                        titleTypographyProps={{
                            variant: "h5",
                            fontWeight: "500",
                            color: "text.primary",
                        }}
                        subheader="This section gives you key updates on the ENA Generation and Demand Assert Registers"
                    />
                </Grid>
                <Grid md={6} xs={12}>
                    <ListDevices
                        cardTitle="Compliant Devices"
                        cardDescription="These are the last 10 devices that have been tagged as compliant by our evaluators"
                        cardTooltip={TT_DEVICES_APPROVED}
                        data={data.topCompliantSummary}
                    />
                </Grid>
                <Grid md={6} xs={12}>
                    <ListDevices
                        cardTitle="Latest Alerts"
                        cardDescription="These are the latest devices with alerts. Alerts can be raised by DNOs, ENA or the device owner and are used to notify of potential issues with the device"
                        cardTooltip={TT_DEVICES_ALERT}
                        data={data.topLiveAlerts.map((alert) => ({
                            ...alert,
                            systemReference: alert.deviceSystemRef,
                        }))}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default DnoHomepage
